package com.iregained.site.components.sections.patients.sections

// Styles separation: Import styles from PatientOverviewHeroStyle.kt
import androidx.compose.runtime.*
import com.iregained.site.components.sections.patients.styles.EducationDiagramStyle
import com.iregained.site.components.sections.patients.styles.EducationListItemStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.*
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.Cursor.Companion.Default
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Converging Blur
@Composable
private fun Modifier.convergingBlur(isVisible: Boolean, isLeft: Boolean, delayMs: Int = 0): Modifier {
    val startX = if (isLeft) (-50).px else 50.px
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else startX)
        .styleModifier { property("filter", if (isVisible) "blur(0px)" else "blur(8px)") }.transition(
            Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("filter", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun PatientsScienceEducationSection(data: PatientsScienceEducationSectionData = defaultPatientsScienceEducationSectionData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "patients-science-education"
    var isVisible by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
            .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
            .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1440.px), horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (data.label.isNotBlank()) {
                    SectionHeader(
                        text = data.label,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 40.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s)),
                        alignment = Alignment.CenterHorizontally
                    )
                }

                if (isDesktop) {
                    Row(
                        modifier = Modifier.fillMaxWidth().gap(100.px).padding(bottom = 25.px),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Box(
                            modifier = Modifier.weight(1.0f).minWidth(0.px).convergingBlur(isVisible, isLeft = true),
                            contentAlignment = Alignment.CenterStart
                        ) {
                            EducationDiagram(data)
                        }
                        Column(
                            modifier = Modifier.weight(1.2f).minWidth(0.px).align(Alignment.Top)
                                .convergingBlur(isVisible, isLeft = false, delayMs = 200)
                        ) {
                            EducationContent(data, data.bulletIconPath, palette, Modifier.padding(left = 20.px))
                        }
                    }
                } else {
                    Column(
                        modifier = Modifier.fillMaxWidth().maxWidth(1200.px),
                        verticalArrangement = Arrangement.spacedBy(50.px),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        EducationContent(
                            data,
                            data.bulletIconPath,
                            palette,
                            Modifier.fillMaxWidth().convergingBlur(isVisible, isLeft = true),
                            isMobile = true
                        )
                        EducationDiagram(
                            data, Modifier.fillMaxWidth().convergingBlur(isVisible, isLeft = false, delayMs = 200)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun EducationContent(
    data: PatientsScienceEducationSectionData,
    bulletIconPath: String,
    palette: SitePalette,
    modifier: Modifier,
    isMobile: Boolean = false
) {
    val align = if (isMobile) Alignment.CenterHorizontally else Alignment.Start
    Column(modifier = modifier, horizontalAlignment = align) {
        if (data.title.isNotBlank()) SpanText(
            text = data.title,
            modifier = HeadlineTextStyle.toModifier().color(palette.custom.darkText).opacity(0.75f).fontSize(30.px)
                .fontWeight(FontWeight.Bold).margin(bottom = 16.px)
        )
        if (data.description.isNotBlank()) Div(
            attrs = SubheadlineTextStyle.toModifier().color(palette.custom.darkText).fontSize(18.px)
                .fontWeight(FontWeight.Medium).margin(bottom = 40.px)
                .toAttrs { ref { htmlElement -> htmlElement.innerHTML = data.description; onDispose {} } })
        Column(modifier = Modifier.gap(20.px)) {
            data.keyTakeaways.forEach { point ->
                key(point) {
                    if (point.isNotBlank()) {
                        Row(
                            verticalAlignment = Alignment.Top,
                            modifier = EducationListItemStyle.toModifier().cursor(Default)
                        ) {
                            Image(
                                src = bulletIconPath,
                                modifier = Modifier.unextractable().size(16.px).margin(right = 10.px, top = 7.px)
                            )
                            Div(
                                attrs = Modifier.fontWeight(FontWeight.Medium)
                                    .toAttrs { ref { htmlElement -> htmlElement.innerHTML = point; onDispose {} } })
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun EducationDiagram(data: PatientsScienceEducationSectionData, modifier: Modifier = Modifier) {
    if (data.imagePath.isNotBlank()) {
        Image(
            src = data.imagePath,
            alt = data.imageAlt,
            modifier = EducationDiagramStyle.toModifier().unextractable().then(modifier).fillMaxWidth()
                .maxHeight(800.px).borderRadius(16.px).objectFit(ObjectFit.Cover)
        )
    }
}

// ==========================================
// DATA MODELS & CMS IMAGE SPECS
// ==========================================

/**
 * CMS IMAGE UPLOAD SPECS FOR NEUROPLASTICITY EDUCATION:
 * @field imagePath:
 * - Aspect Ratio: 1:1 (Square) or slightly vertical (4:5).
 * - Recommended Dimensions: 600x600px.
 * - Format: WebP or high-quality PNG/JPEG.
 * - Content: The "glowing brain" graphic from the old site, or a modern medical illustration of neural pathways.
 */
data class PatientsScienceEducationSectionData(
    val sectionId: String? = null,
    val label: String,
    val title: String,
    val description: String,
    val bulletIconPath: String,
    val keyTakeaways: List<String>,
    val imagePath: String,
    val imageAlt: String
)

val defaultPatientsScienceEducationSectionData = PatientsScienceEducationSectionData(
    sectionId = "patients-science-education",
    label = "The Intelligence Behind Our Innovation",
    title = "Understanding Neuroplasticity",
    description = "The brain has an amazing ability to reorganize itself by forming new neural connections throughout life. This is called neuroplasticity. Following a stroke, your brain can \"re-wire\" itself to bypass damaged areas and learn to control your hand and arm movements again.",
    bulletIconPath = Res.Images.PatientsPage.NeuroplasticityEducation.ICON_CHECK,
    keyTakeaways = listOf(
        "Lifelong Adaptability: Your brain is highly adaptable and capable of learning new neural pathways at any age.",
        "Targeted Stimulation: Repetitive, purposeful movement is the fundamental key to triggering this critical rewiring process.",
        "Optimized Recovery: The MyHand<sup>®</sup> System harnesses this previously untapped potential, using clinically-proven therapy protocols and intelligent biofeedback to restore independent hand function."
    ),
    // NOTE: You will need to add this path to your AppConstants.kt -> Res.Images.Patients object
    imagePath = Res.Images.PatientsPage.NeuroplasticityEducation.neuroplasticity_edu,
    imageAlt = "Abstract illustration of a glowing brain representing neuroplasticity and neural connections"
)